import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  ModalDialog,
  Typography,
  Button,
  Table,
  Input,
  FormControl,
  FormLabel,
  Alert,
  Card,
  Select,
  Option
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';

const today = () => new Date().toISOString().slice(0, 10);
// datetime-local value: YYYY-MM-DDTHH:mm (no seconds)
const nowDateTimeLocal = () => {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${date}T${time}`;
};

const MaterialPopup = ({ materialName, onClose }) => {
  const [batches, setBatches] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consumptionModalBatch, setConsumptionModalBatch] = useState(null);
  const [consumptionForm, setConsumptionForm] = useState({ qty: '', datetime: nowDateTimeLocal() });
  const [consumingBatchId, setConsumingBatchId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [addStockForm, setAddStockForm] = useState({
    purchase_date: today(),
    quantity: '',
    rate: '',
    manufacture_date: '',
    expiry_date: '',
    vendor_id: ''
  });
  const [addStockSubmitting, setAddStockSubmitting] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({
    purchase_date: '',
    quantity: '',
    rate: '',
    manufacture_date: '',
    expiry_date: '',
    vendor_id: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({ changeAmount: '', date: '', time: '00:00', changeNote: '' });
  const [historyEditSubmitting, setHistoryEditSubmitting] = useState(false);
  const [historyDeleteConfirm, setHistoryDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, [materialName]);

  useEffect(() => {
    if ((showAddStock || editingBatch) && vendors.length === 0) {
      fetchVendors();
    }
  }, [showAddStock, editingBatch]);

  const openEditModal = (batch) => {
    setEditingBatch(batch);
    setEditForm({
      material_name: materialName,
      purchase_date: batch.purchase_date ? batch.purchase_date.slice(0, 10) : '',
      quantity: String(batch.quantity ?? ''),
      rate: batch.rate != null && batch.rate !== '' ? String(batch.rate) : '',
      manufacture_date: batch.manufacture_date ? batch.manufacture_date.slice(0, 10) : '',
      expiry_date: batch.expiry_date ? batch.expiry_date.slice(0, 10) : '',
      vendor_id: batch.vendor_id != null ? String(batch.vendor_id) : ''
    });
    setError('');
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target || e;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBatch) return;
    if (!editForm.purchase_date) {
      setError('Purchase date is required');
      return;
    }
    const qty = parseInt(editForm.quantity, 10);
    if (Number.isNaN(qty) || qty < 0) {
      setError('Quantity must be 0 or greater');
      return;
    }
    if (!editForm.vendor_id) {
      setError('Vendor is required');
      return;
    }
    const trimmedName = editForm.material_name?.trim();
    if (!trimmedName) {
      setError('Material name is required');
      return;
    }
    try {
      setEditSubmitting(true);
      setError('');
      await axiosInstance.put(`/materials/${editingBatch.id}`, {
        material_name: trimmedName,
        purchase_date: editForm.purchase_date,
        quantity: qty,
        rate: editForm.rate !== '' ? editForm.rate : null,
        manufacture_date: editForm.manufacture_date || null,
        expiry_date: editForm.expiry_date || null,
        vendor_id: parseInt(editForm.vendor_id, 10)
      });
      setEditingBatch(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update batch');
    } finally {
      setEditSubmitting(false);
    }
  };

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(`/materials/batches/${encodeURIComponent(materialName)}`);
      const fetchedBatches = response.data.batches || [];
      setBatches(fetchedBatches);

      if (fetchedBatches.length > 0) {
        fetchHistory(fetchedBatches);
      } else {
        setHistory([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (batchesToUse) => {
    try {
      // Fetch history for all batches
      const historyPromises = batchesToUse.map(batch =>
        axiosInstance.get(`/materials/${batch.id}/history`)
      );
      const responses = await Promise.all(historyPromises);
      const allHistory = responses.flatMap((res, i) => {
        const list = res.data.history || [];
        const batchId = batchesToUse[i]?.id;
        return list.map(h => ({ ...h, material_id: h.material_id ?? batchId }));
      });
      // Sort by id descending (latest first)
      allHistory.sort((a, b) => (b.id - a.id));
      setHistory(allHistory);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const openConsumptionModal = (batch) => {
    setConsumptionModalBatch(batch);
    setConsumptionForm({ qty: '', datetime: nowDateTimeLocal() });
    setShowReturnConfirm(false);
    setError('');
  };

  const handleReturnConfirm = () => {
    handleConsumptionSubmit('returned');
    setShowReturnConfirm(false);
  };

  const handleConsumptionSubmit = async (action) => {
    if (!consumptionModalBatch) return;
    const num = parseInt(consumptionForm.qty, 10);
    if (!num || num <= 0) {
      setError('Enter a valid quantity');
      return;
    }
    if (action === 'consumed' && isExpired(consumptionModalBatch.expiry_date)) {
      setError('Expired stock cannot be consumed; use Return only');
      return;
    }
    if (action === 'consumed' && num > consumptionModalBatch.quantity) {
      setError('Quantity cannot exceed current stock');
      return;
    }
    const raw = (consumptionForm.datetime || nowDateTimeLocal()).trim();
    const datePart = raw.slice(0, 10);
    if (datePart > today()) {
      setError('Date cannot be in the future');
      return;
    }
    const consumptionDateTime = raw.includes('T')
      ? `${raw.slice(0, 10)} ${raw.slice(11, 16)}:00`
      : `${datePart} 00:00:00`;
    const isReturned = action === 'returned';
    const changeAmount = -Math.abs(num); // both Consumed and Returned reduce stock
    const changeNote = isReturned ? 'Material returned' : 'Consumed';
    try {
      setConsumingBatchId(consumptionModalBatch.id);
      setError('');
      await axiosInstance.put(`/materials/${consumptionModalBatch.id}/quantity`, {
        changeAmount,
        consumptionDate: consumptionDateTime,
        changeNote
      });
      setConsumptionModalBatch(null);
      setConsumptionForm({ qty: '', datetime: nowDateTimeLocal() });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || (isReturned ? 'Failed to record return' : 'Failed to record consumption'));
    } finally {
      setConsumingBatchId(null);
    }
  };

  const handleAddStockChange = (e) => {
    const { name, value } = e.target || e;
    setAddStockForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!addStockForm.quantity || !addStockForm.vendor_id || parseInt(addStockForm.quantity, 10) <= 0) {
      setError('Quantity and vendor are required');
      return;
    }
    try {
      setAddStockSubmitting(true);
      setError('');
      await axiosInstance.post('/materials', {
        material_name: materialName,
        purchase_date: addStockForm.purchase_date,
        quantity: parseInt(addStockForm.quantity, 10),
        rate: addStockForm.rate !== '' ? addStockForm.rate : null,
        manufacture_date: addStockForm.manufacture_date || null,
        expiry_date: addStockForm.expiry_date || null,
        vendor_id: parseInt(addStockForm.vendor_id, 10)
      });
      setShowAddStock(false);
      setAddStockForm({
        purchase_date: today(),
        quantity: '',
        rate: '',
        manufacture_date: '',
        expiry_date: '',
        vendor_id: ''
      });
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock');
    } finally {
      setAddStockSubmitting(false);
    }
  };

  const openHistoryEdit = (item) => {
    setEditingHistoryItem(item);
    let timeStr = '00:00';
    if (item.date) {
      const d = new Date(item.date);
      if (!Number.isNaN(d.getTime())) {
        timeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    }
    setHistoryEditForm({
      changeAmount: String(item.change_amount),
      date: item.date ? item.date.slice(0, 10) : today(),
      time: timeStr,
      changeNote: item.change_note || ''
    });
    setError('');
  };

  const handleHistoryEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingHistoryItem || editingHistoryItem.material_id == null) return;
    const amount = parseInt(historyEditForm.changeAmount, 10);
    if (Number.isNaN(amount)) {
      setError('Quantity change must be a number');
      return;
    }
    try {
      setHistoryEditSubmitting(true);
      setError('');
      const datePart = historyEditForm.date || today();
      const timePart = (historyEditForm.time || '00:00').trim();
      const changeDateTime = timePart.match(/^\d{1,2}:\d{2}$/) ? `${datePart} ${timePart}:00` : `${datePart} 00:00:00`;
      await axiosInstance.put(
        `/materials/${editingHistoryItem.material_id}/history/${editingHistoryItem.id}`,
        {
          changeAmount: amount,
          changeDate: changeDateTime,
          changeNote: historyEditForm.changeNote?.trim() || null
        }
      );
      setEditingHistoryItem(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update history');
    } finally {
      setHistoryEditSubmitting(false);
    }
  };

  const handleHistoryDelete = async () => {
    if (!historyDeleteConfirm) return;
    const { material_id, id } = historyDeleteConfirm;
    try {
      setError('');
      await axiosInstance.delete(`/materials/${material_id}/history/${id}`);
      setHistoryDeleteConfirm(null);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete history');
    } finally {
      setHistoryDeleteConfirm(null);
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      setDeleting(true);
      setError('');
      await axiosInstance.put('/materials/mark-deleted', { materialName });
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove material');
    } finally {
      setDeleting(false);
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysToExpire = (expiryDate, asOfDate) => {
    if (!expiryDate) return '—';
    const expiry = new Date(expiryDate);
    const ref = asOfDate ? new Date(asOfDate) : new Date();
    const ms = expiry.getTime() - ref.getTime();
    const days = Math.round(ms / (24 * 60 * 60 * 1000));
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    return 'Expires today';
  };

  return (
    <>
      <Modal open={true} onClose={onClose}>
        <ModalDialog
          sx={{
            maxWidth: '90vw',
            width: '1200px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography level="h2" sx={{ color: '#eb7a24' }}>
              {materialName} - Batch Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                color="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Remove from list
              </Button>
              <Button variant="plain" onClick={onClose}>
                Close
              </Button>
            </Box>
          </Box>

          {showDeleteConfirm && (
            <Alert
              color="neutral"
              variant="soft"
              sx={{ mb: 2 }}
              endDecorator={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="sm" variant="plain" color="neutral" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="solid" color="danger" onClick={handleDeleteMaterial} loading={deleting}>
                    Remove
                  </Button>
                </Box>
              }
            >
              Remove this material from the list? It will be marked as deleted and hidden. You can add the same material again later.
            </Alert>
          )}

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography level="h3">
                Batches
              </Typography>
              <Button
                size="sm"
                onClick={() => setShowAddStock((v) => !v)}
                sx={{
                  backgroundColor: '#eb7a24',
                  '&:hover': { backgroundColor: '#cc6a1f' }
                }}
              >
                {showAddStock ? 'Cancel' : 'Add stock'}
              </Button>
            </Box>

            {showAddStock && (
              <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fffbf7' }}>
                <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
                  Add new batch
                </Typography>
                <form onSubmit={handleAddStockSubmit}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Material</FormLabel>
                      <Input value={materialName} disabled size="sm" />
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Purchase Date</FormLabel>
                      <Input
                        type="date"
                        name="purchase_date"
                        value={addStockForm.purchase_date}
                        onChange={handleAddStockChange}
                        size="sm"
                        required
                        sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ width: 100 }}>
                      <FormLabel>Quantity</FormLabel>
                      <Input
                        type="number"
                        name="quantity"
                        value={addStockForm.quantity}
                        onChange={handleAddStockChange}
                        size="sm"
                        min="1"
                        required
                        placeholder="0"
                        sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ width: 100 }}>
                      <FormLabel>Rate (₹)</FormLabel>
                      <Input
                        type="number"
                        name="rate"
                        value={addStockForm.rate}
                        onChange={handleAddStockChange}
                        size="sm"
                        min="0"
                        step="0.01"
                        placeholder="0"
                        sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Manufacturing Date</FormLabel>
                      <Input
                        type="date"
                        name="manufacture_date"
                        value={addStockForm.manufacture_date}
                        onChange={handleAddStockChange}
                        size="sm"
                        sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                      <FormLabel>Expiry Date</FormLabel>
                      <Input
                        type="date"
                        name="expiry_date"
                        value={addStockForm.expiry_date}
                        onChange={handleAddStockChange}
                        size="sm"
                        sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 180 }}>
                      <FormLabel>Vendor</FormLabel>
                      <Select
                        name="vendor_id"
                        value={addStockForm.vendor_id || ''}
                        onChange={(_, val) => handleAddStockChange({ target: { name: 'vendor_id', value: val } })}
                        size="sm"
                        placeholder={vendorsLoading ? 'Loading...' : 'Select vendor'}
                        disabled={vendorsLoading}
                        required
                        sx={{ minWidth: 160, '&.Joy-focused': { borderColor: '#ea7a23' } }}
                      >
                        {vendors.map((v) => (
                          <Option key={v.id} value={String(v.id)}>{v.name}</Option>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      type="submit"
                      size="sm"
                      loading={addStockSubmitting}
                      sx={{
                        alignSelf: 'flex-end',
                        backgroundColor: '#eb7a24',
                        '&:hover': { backgroundColor: '#cc6a1f' }
                      }}
                    >
                      Add stock
                    </Button>
                  </Box>
                </form>
              </Card>
            )}

            <Box sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  '& thead th': {
                    backgroundColor: '#eb7a24',
                    color: 'white',
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  },
                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#f9f9f9'
                  },
                  '& tbody tr:hover': {
                    backgroundColor: '#fff4e6'
                  },
                  '& td': {
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }
                }}
              >
                <thead>
                  <tr>
                    <th>Purchase Date</th>
                    <th>Days to expire</th>
                    <th>Vendor</th>
                    <th>Rate (per item)</th>
                    <th>Current Quantity</th>
                    <th>Last Updated</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                        Loading...
                      </td>
                    </tr>
                  ) : batches.filter((b) => Number(b.quantity) > 0).length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                        {batches.length === 0
                          ? 'No batches found. Use "Add stock" to add a new batch.'
                          : 'No batches with stock. Use "Add stock" to add a new batch.'}
                      </td>
                    </tr>
                  ) : (
                    batches.filter((b) => Number(b.quantity) > 0).map((batch) => (
                      <tr
                        key={batch.id}
                        style={{
                          backgroundColor: isExpired(batch.expiry_date) ? '#ffebee' : 'transparent'
                        }}
                      >
                        <td>
                          {batch.purchase_date
                            ? new Date(batch.purchase_date).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>
                          <Typography
                            level="body-sm"
                            sx={isExpired(batch.expiry_date) ? { color: 'danger', fontWeight: 500 } : {}}
                          >
                            {getDaysToExpire(batch.expiry_date)}
                          </Typography>
                        </td>
                        <td>{batch.vendor_name || 'N/A'}</td>
                        <td>{batch.rate != null ? Number(batch.rate).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                        <td>{batch.quantity}</td>
                        <td>
                          {batch.last_updated
                            ? new Date(batch.last_updated).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td>{batch.created_by || '—'}</td>
                        <td>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(batch);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              onClick={() => openConsumptionModal(batch)}
                              loading={consumingBatchId === batch.id}
                            >
                              Consumed
                            </Button>
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Box>
          </Card>

          <Card>
            <Typography level="h3" sx={{ mb: 2 }}>
              Change History
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  '& thead th': {
                    backgroundColor: '#eb7a24',
                    color: 'white',
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  },
                  '& tbody tr:nth-of-type(even)': {
                    backgroundColor: '#f9f9f9'
                  },
                  '& tbody tr:hover': {
                    backgroundColor: '#fff4e6'
                  },
                  '& td': {
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }
                }}
              >
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Type</th>
                    <th>Quantity Change</th>
                    <th>Available Quantity</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                        No history found
                      </td>
                    </tr>
                  ) : (
                    history.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {new Date(item.date).toLocaleDateString()}
                        </td>
                        <td>{item.vendor_name || 'N/A'}</td>
                        <td>{item.change_note || (item.change_amount > 0 ? 'Added' : 'Consumed')}</td>
                        <td
                          style={{
                            color: item.change_amount > 0 ? 'green' : 'red',
                            fontWeight: 'bold'
                          }}
                        >
                          {item.change_amount > 0 ? '+' : ''}
                          {item.change_amount}
                        </td>
                        <td>{item.new_quantity}</td>
                        <td>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button
                              size="sm"
                              variant="plain"
                              color="neutral"
                              onClick={() => openHistoryEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="plain"
                              color="danger"
                              onClick={() => setHistoryDeleteConfirm(item)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Box>
          </Card>
        </ModalDialog>
      </Modal>

      {editingBatch && (
        <Modal open onClose={() => setEditingBatch(null)}>
          <ModalDialog sx={{ maxWidth: 600 }}>
            <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
              Edit batch
            </Typography>
            <form onSubmit={handleEditSubmit}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl sx={{ flex: 1, minWidth: 200 }} required>
                  <FormLabel>Material Name</FormLabel>
                  <Input
                    type="text"
                    name="material_name"
                    value={editForm.material_name}
                    onChange={handleEditFormChange}
                    size="sm"
                    placeholder="e.g. Surgical Gloves"
                    required
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 140 }} required>
                  <FormLabel>Purchase Date</FormLabel>
                  <Input
                    type="date"
                    name="purchase_date"
                    value={editForm.purchase_date}
                    onChange={handleEditFormChange}
                    size="sm"
                    required
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ width: 100 }} required>
                  <FormLabel>Quantity</FormLabel>
                  <Input
                    type="number"
                    name="quantity"
                    value={editForm.quantity}
                    onChange={handleEditFormChange}
                    size="sm"
                    min="0"
                    required
                    placeholder="0"
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ width: 100 }}>
                  <FormLabel>Rate (₹)</FormLabel>
                  <Input
                    type="number"
                    name="rate"
                    value={editForm.rate}
                    onChange={handleEditFormChange}
                    size="sm"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 140 }}>
                  <FormLabel>Manufacturing Date</FormLabel>
                  <Input
                    type="date"
                    name="manufacture_date"
                    value={editForm.manufacture_date}
                    onChange={handleEditFormChange}
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 140 }}>
                  <FormLabel>Expiry Date</FormLabel>
                  <Input
                    type="date"
                    name="expiry_date"
                    value={editForm.expiry_date}
                    onChange={handleEditFormChange}
                    size="sm"
                    sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                  />
                </FormControl>
                <FormControl sx={{ minWidth: 180 }} required>
                  <FormLabel>Vendor</FormLabel>
                  <Select
                    name="vendor_id"
                    value={editForm.vendor_id || ''}
                    onChange={(_, val) => handleEditFormChange({ target: { name: 'vendor_id', value: val ?? '' } })}
                    size="sm"
                    placeholder={vendorsLoading ? 'Loading...' : 'Select vendor'}
                    disabled={vendorsLoading}
                    required
                    sx={{ minWidth: 160, '&.Joy-focused': { borderColor: '#ea7a23' } }}
                  >
                    {editingBatch?.vendor_id != null && editingBatch.vendor_name && !vendors.some((v) => Number(v.id) === Number(editingBatch.vendor_id)) && (
                      <Option value={String(editingBatch.vendor_id)}>{editingBatch.vendor_name}</Option>
                    )}
                    {vendors.map((v) => (
                      <Option key={v.id} value={String(v.id)}>{v.name}</Option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="button"
                  variant="plain"
                  color="neutral"
                  onClick={() => setEditingBatch(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={editSubmitting}
                  sx={{
                    backgroundColor: '#eb7a24',
                    '&:hover': { backgroundColor: '#cc6a1f' }
                  }}
                >
                  Save
                </Button>
              </Box>
            </form>
          </ModalDialog>
        </Modal>
      )}

      {consumptionModalBatch && (
        <Modal open onClose={() => setConsumptionModalBatch(null)}>
          <ModalDialog sx={{ maxWidth: 400 }}>
            <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
              {isExpired(consumptionModalBatch.expiry_date) ? 'Return (expired – cannot consume)' : 'Consumed / Return'}
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              Batch: {consumptionModalBatch.purchase_date
                ? new Date(consumptionModalBatch.purchase_date).toLocaleDateString()
                : 'N/A'}
              {' · '}Current qty: {consumptionModalBatch.quantity}
              {isExpired(consumptionModalBatch.expiry_date) && (
                <Typography component="span" level="body-sm" sx={{ color: 'danger', ml: 0.5 }}>
                  · Expired
                </Typography>
              )}
            </Typography>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); }}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Quantity * (max {consumptionModalBatch.quantity})</FormLabel>
                <Input
                  type="number"
                  value={consumptionForm.qty}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setConsumptionForm((prev) => ({ ...prev, qty: '' }));
                      return;
                    }
                    const num = parseInt(raw, 10);
                    const available = Number(consumptionModalBatch.quantity) || 0;
                    if (!Number.isNaN(num) && num > available) {
                      setConsumptionForm((prev) => ({ ...prev, qty: String(available) }));
                    } else {
                      setConsumptionForm((prev) => ({ ...prev, qty: raw }));
                    }
                  }}
                  min={1}
                  max={consumptionModalBatch.quantity}
                  placeholder="0"
                  required
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                  Max {consumptionModalBatch.quantity} available for this batch
                </Typography>
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Date & time</FormLabel>
                <Input
                  type="datetime-local"
                  value={consumptionForm.datetime}
                  max={`${today()}T23:59`}
                  onChange={(e) => setConsumptionForm((prev) => ({ ...prev, datetime: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                  Cannot select a future date
                </Typography>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  variant="plain"
                  color="neutral"
                  onClick={() => setConsumptionModalBatch(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="success"
                  onClick={() => setShowReturnConfirm(true)}
                  loading={consumingBatchId === consumptionModalBatch.id}
                  disabled={
                    (() => {
                      const n = parseInt(consumptionForm.qty, 10);
                      return Number.isNaN(n) || n <= 0 || n > Number(consumptionModalBatch.quantity);
                    })()
                  }
                >
                  Returned
                </Button>
                {!isExpired(consumptionModalBatch.expiry_date) && (
                  <Button
                    type="button"
                    sx={{
                      backgroundColor: '#eb7a24',
                      '&:hover': { backgroundColor: '#cc6a1f' }
                    }}
                    onClick={() => handleConsumptionSubmit('consumed')}
                    loading={consumingBatchId === consumptionModalBatch.id}
                    disabled={
                      (() => {
                        const n = parseInt(consumptionForm.qty, 10);
                        return Number.isNaN(n) || n <= 0 || n > Number(consumptionModalBatch.quantity);
                      })()
                    }
                  >
                    Consumed
                  </Button>
                )}
              </Box>
            </Box>
          </ModalDialog>
        </Modal>
      )}

      {/* Full-screen return confirmation */}
      <Modal
        open={showReturnConfirm}
        onClose={() => setShowReturnConfirm(false)}
        sx={{ zIndex: 1400 }}
        slotProps={{
          backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } }
        }}
      >
        <ModalDialog
          layout="center"
          variant="soft"
          color="warning"
          sx={{
            maxWidth: 400,
            boxShadow: 'lg',
            p: 3
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>
            Confirm return
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Confirm return of {consumptionForm.qty || '0'} quantity? Stock will be reduced.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setShowReturnConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="warning"
              onClick={handleReturnConfirm}
              loading={consumingBatchId === consumptionModalBatch?.id}
            >
              Confirm return
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Full-screen delete history confirmation */}
      <Modal
        open={Boolean(historyDeleteConfirm)}
        onClose={() => setHistoryDeleteConfirm(null)}
        sx={{ zIndex: 1400 }}
        slotProps={{
          backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.6)' } }
        }}
      >
        <ModalDialog
          layout="center"
          variant="soft"
          color="danger"
          sx={{
            maxWidth: 400,
            boxShadow: 'lg',
            p: 3
          }}
        >
          <Typography level="h4" sx={{ mb: 2 }}>
            Delete history record
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Delete this history record? Stock will be adjusted accordingly.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={() => setHistoryDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={handleHistoryDelete}>
              Delete
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {editingHistoryItem && (
        <Modal open onClose={() => setEditingHistoryItem(null)}>
          <ModalDialog sx={{ maxWidth: 440 }}>
            <Typography level="h4" sx={{ mb: 2, color: '#eb7a24' }}>
              Edit history record
            </Typography>
            <form onSubmit={handleHistoryEditSubmit}>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Quantity change * (negative = consumed/returned, positive = added)</FormLabel>
                <Input
                  type="number"
                  value={historyEditForm.changeAmount}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, changeAmount: e.target.value }))}
                  required
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Date</FormLabel>
                <Input
                  type="date"
                  value={historyEditForm.date}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, date: e.target.value }))}
                  max={today()}
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Time</FormLabel>
                <Input
                  type="time"
                  value={historyEditForm.time}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, time: e.target.value }))}
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }} fullWidth>
                <FormLabel>Type / Note</FormLabel>
                <Input
                  value={historyEditForm.changeNote}
                  onChange={(e) => setHistoryEditForm((prev) => ({ ...prev, changeNote: e.target.value }))}
                  placeholder="e.g. Consumed, Material returned"
                  sx={{ '&:focus-within': { borderColor: '#ea7a23' } }}
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button type="button" variant="plain" color="neutral" onClick={() => setEditingHistoryItem(null)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={historyEditSubmitting}
                  sx={{ backgroundColor: '#eb7a24', '&:hover': { backgroundColor: '#cc6a1f' } }}
                >
                  Save
                </Button>
              </Box>
            </form>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default MaterialPopup;

